import { useSeoCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import { Button } from "@/design-system/Button/Button";
import { Dropdown, DropdownItem } from "@/design-system/Dropdown/Dropdown";
import { InfoTip } from "@/design-system/InfoTip/InfoTip";
import Input from "@/design-system/Input/Input";
import { CategoryTab } from "@/design-system/Tabs/CategoryTab";
import { useStartClubImport } from "@/features/MyClub/EmptyClub/useStartClubImport";
import {
  globalStateSelector,
  topToPageSelector,
} from "@/redux/globalState/globalStateSlice";
import {
  fetchObjectiveGroups,
  getFilteredObjectiveGroupsSelector,
  getObjectiveCategoriesSelector,
  getObjectivesSelector,
  updateObjectiveCategory,
  updateObjectiveSearchTerm,
} from "@/redux/objectives/objectivesSlice";
import { AppDispatch } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { formatTime } from "@repo/shared/utils";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import {
  ObjectiveBundleButton,
  ObjectiveBundleCustomizeButton,
} from "./ObjectiveBundleBar";
import { ObjectiveGroupCard } from "./ObjectiveGroupCard";

type ObjectiveGroupStatus = "all" | "available" | "completed" | "locked";

const CLUB_IMPORT_ORIGIN = { section: "Objectives", subsection: "List" };

const STATUS_LABEL_KEYS = {
  all: "objectives:list.status.all",
  available: "objectives:list.status.available",
  completed: "objectives:list.status.completed",
  locked: "objectives:list.status.locked",
} as const;

// Modelled on the SBC sets page: title, search band with the status filter,
// bundle actions, category tabs, card grid.
const ObjectivesPage = () => {
  const { t } = useTranslation(["objectives", "common"]);
  const objectivesSeo = useSeoCopy("objectives");
  const dispatch = useDispatch<AppDispatch>();
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const { groups, categoryId, searchTerm } = useSelector(getObjectivesSelector);
  const categories = useSelector(getObjectiveCategoriesSelector);
  const filteredGroups = useSelector(getFilteredObjectiveGroupsSelector);
  const topToPage = useSelector(topToPageSelector);
  const { lastImportFinishedAt } = useSelector(globalStateSelector);
  const [status, setStatus] = useState<ObjectiveGroupStatus>("available");
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const { pathname } = useLocation();
  const startClubImport = useStartClubImport(CLUB_IMPORT_ORIGIN);

  const groupsByStatus: Record<ObjectiveGroupStatus, typeof filteredGroups> = {
    all: filteredGroups,
    available: filteredGroups.filter(
      (group) => !group.completed && !group.locked,
    ),
    completed: filteredGroups.filter((group) => group.completed),
    locked: filteredGroups.filter((group) => group.locked),
  };
  const shownGroups = groupsByStatus[status];
  // As on the SBC page: counts on desktop, the label alone on a phone.
  const statusOptions: DropdownItem[] = (
    Object.keys(STATUS_LABEL_KEYS) as ObjectiveGroupStatus[]
  ).map((value) => ({
    value,
    label: isTouchDevice
      ? t(STATUS_LABEL_KEYS[value])
      : `${t(STATUS_LABEL_KEYS[value])} (${groupsByStatus[value].length})`,
  }));

  // In an effect, not a route loader: loaders run before the shared API
  // instance gets its base URL. Refetched after a club import, which syncs the
  // user's objective progress.
  useEffect(() => {
    dispatch(fetchObjectiveGroups());
  }, [dispatch, lastImportFinishedAt]);

  const bundleControls = (
    <>
      <span className="text-sm font-semibold text-foreground">
        {t("objectives:bar.title")}
      </span>
      <InfoTip label={t("objectives:bar.title")}>
        {t("objectives:bar.info")}
      </InfoTip>
      <ObjectiveBundleCustomizeButton />
      <ObjectiveBundleButton />
    </>
  );

  return (
    <main className="z-10 h-full min-h-screen flex-1 bg-background text-white">
      <HeroSection
        helmetTitle={objectivesSeo.title}
        helmetDescription={objectivesSeo.description}
        shouldBeInTheDocumentFlow={true}
      />
      <div
        className="sticky z-20 flex h-15 w-full flex-row justify-center bg-surface py-2 touch-device:py-4"
        style={{ top: topToPage.value }}
      >
        <div className="mx-auto flex w-full max-w-content items-center justify-between gap-x-4 px-4 large-desktop:px-8 touch-device:pr-16">
          <div className="flex min-w-0 items-center gap-x-4">
            <div className="w-54 min-w-0 shrink">
              <Input
                type="search"
                size="small"
                icon={<Search className="size-4" />}
                placeholder={t("common:search")}
                value={searchTerm}
                onChange={(value) => dispatch(updateObjectiveSearchTerm(value))}
                onClear={() => dispatch(updateObjectiveSearchTerm(""))}
              />
            </div>
            {!isTouchDevice && (
              <div className="flex shrink-0 items-center gap-x-2">
                {bundleControls}
              </div>
            )}
          </div>
          <Dropdown
            variant="secondary"
            hideSearch
            width={isTouchDevice ? 118 : 150}
            fitContent
            align="end"
            items={statusOptions}
            selected={status}
            placeholder={t(STATUS_LABEL_KEYS[status])}
            onChange={(item) => setStatus(item.value as ObjectiveGroupStatus)}
          />
        </div>
      </div>
      {/* Touch devices have no room in the bar: a row of its own. The rule sits
          on the inner row so it spans the grid, not the page gutters. */}
      {isTouchDevice && (
        <div className="mx-auto w-full max-w-content px-4 pt-3 large-desktop:px-8">
          <div className="flex items-center gap-x-2 border-b border-neutral-800 pb-3">
            {bundleControls}
          </div>
        </div>
      )}
      <div className="mx-auto flex w-full max-w-content items-center gap-x-2 overflow-x-auto px-4 pt-3 large-desktop:px-8">
        <div className="-ml-2 flex items-center gap-x-2">
          <CategoryTab
            label={t("objectives:list.all")}
            isActive={categoryId === null}
            onClick={() => dispatch(updateObjectiveCategory(null))}
          />
          {categories.map(({ id, name }) => (
            <CategoryTab
              key={id}
              label={name ?? t("objectives:list.category", { id })}
              isActive={categoryId === id}
              onClick={() => dispatch(updateObjectiveCategory(id))}
            />
          ))}
        </div>
        <div className="flex-1" />
        {/* Signed in: how fresh the progress is, or how to get it. */}
        {isLoggedIn &&
          (groups.progressUpdatedAt ? (
            <span className="shrink-0 text-xs text-foreground-faint">
              {t("objectives:list.synced", {
                time: formatTime(
                  Date.now() - Date.parse(groups.progressUpdatedAt),
                ),
              })}
            </span>
          ) : (
            <Button
              variant="tertiary"
              size="small"
              className="shrink-0"
              onClick={() => startClubImport({ returnTo: pathname })}
            >
              {t("objectives:list.importPrompt")}
            </Button>
          ))}
      </div>
      {shownGroups.length > 0 ? (
        <section className="mx-auto grid w-full max-w-content grid-cols-1 gap-4 px-4 pb-12 pt-4 desktop:grid-cols-2 large-desktop:grid-cols-3 large-desktop:px-8">
          {shownGroups.map((group) => (
            <ObjectiveGroupCard key={group.id} group={group} />
          ))}
        </section>
      ) : (
        <p className="mt-4 min-h-[50vh] text-center text-sm text-foreground-muted">
          {groups.status === "pending" || groups.status === "idle" ? null : (
            <span>{t("objectives:list.empty")}</span>
          )}
        </p>
      )}
    </main>
  );
};

export default ObjectivesPage;
